/**
 * Live-reading rules for the Roost wallboard TV (WI 34275 / 34276 / 34277).
 *
 * Tiles re-read their GET when the bridge pushes a change signal, plus a slow
 * bounded reconcile for the few write paths that do not push. A tile that can
 * no longer vouch for its number shows STALE with its last-updated time, and a
 * dead live connection reloads the page, so a frozen number never looks live.
 */

export type WallboardSource =
  | 'quota'
  | 'context'
  | 'workQueue'
  | 'depths'
  | 'reviews'
  | 'owed'
  | 'roster'
  | 'cc';

/** Bridge SignalR targets (RynBridge WallboardPush) and the GETs each one invalidates. */
export const WALLBOARD_PUSH_SOURCES: Readonly<
  Record<string, readonly WallboardSource[]>
> = {
  quotaUpdated: ['quota'],
  contextSnapshot: ['context'],
  openInboundChanged: ['workQueue', 'depths'],
};

/** The gaps-line text for a failed bridge read. */
export function bridgeErrorText(err: unknown): string {
  const e = err as { status?: number; message?: string };
  if (e?.status === 401) return 'Bridge sign-in required';
  if (e?.status === 0) return 'Bridge unreachable';
  return e?.message ?? 'Request failed';
}

/** Pushes arriving within this window collapse into one re-read per source. */
export const PUSH_COALESCE_MS = 2_000;
/** Bounded safety re-read of every source, for write paths with no push. */
export const RECONCILE_MS = 5 * 60_000;
/** Past two missed reconciles a tile can no longer vouch for its number. */
export const STALE_AFTER_MS = 2 * RECONCILE_MS + 60_000;
/** A live connection down this long reloads the page (withAutomaticReconnect has given up). */
export const RELOAD_AFTER_DISCONNECT_MS = 2 * 60_000;
/** Never reload more often than this, so a bridge outage cannot spin the TV. */
export const MIN_RELOAD_INTERVAL_MS = 5 * 60_000;

export type StaleReason = 'disconnected' | 'error' | 'never' | 'aged';

export interface TileFreshness {
  stale: boolean;
  reason: StaleReason | null;
  lastUpdatedAt: number | null;
}

/**
 * Whether a tile can vouch for its number right now. Disconnected wins: with no
 * live connection no push can arrive, whatever the last read said.
 */
export function tileFreshness(input: {
  lastUpdatedAt: number | null | undefined;
  hubConnected: boolean;
  error?: string | null;
  nowMs: number;
}): TileFreshness {
  const lastUpdatedAt = input.lastUpdatedAt ?? null;
  let reason: StaleReason | null = null;
  if (!input.hubConnected) reason = 'disconnected';
  else if (input.error) reason = 'error';
  else if (lastUpdatedAt === null) reason = 'never';
  else if (input.nowMs - lastUpdatedAt > STALE_AFTER_MS) reason = 'aged';
  return { stale: reason !== null, reason, lastUpdatedAt };
}

/**
 * One tile fed by two sources (waiting users + the queue-depth footnote): it is
 * live only when both are, and it vouches for the OLDER of the two reads.
 */
export function combineFreshness(
  a: TileFreshness,
  b: TileFreshness
): TileFreshness {
  const stale = a.stale || b.stale;
  const reason = a.reason ?? b.reason;
  const lastUpdatedAt =
    a.lastUpdatedAt === null || b.lastUpdatedAt === null
      ? null
      : Math.min(a.lastUpdatedAt, b.lastUpdatedAt);
  return { stale, reason, lastUpdatedAt };
}

/**
 * "Last full read": the oldest successful read across every source, or null
 * until each source has loaded once. Never the time a read was merely started.
 */
export function lastFullReadAt(
  lastUpdatedAt: Partial<Record<WallboardSource, number>>,
  sources: readonly WallboardSource[]
): number | null {
  let oldest: number | null = null;
  for (const s of sources) {
    const at = lastUpdatedAt[s];
    if (at === undefined) return null;
    oldest = oldest === null ? at : Math.min(oldest, at);
  }
  return oldest;
}

/**
 * Out-of-order guard: a response applies only when it belongs to a newer
 * request than the last one applied for that source, so an older GET that
 * lands last can never overwrite a newer reading with a fresh clock.
 */
export function isNewerResponse(
  generation: number,
  lastAppliedGeneration: number
): boolean {
  return generation > lastAppliedGeneration;
}

/** Source label for a tile header: "live (push)" or "STALE, updated 12:04 AM". */
export function freshnessLabel(
  f: TileFreshness,
  formatTime: (ms: number) => string
): string {
  if (!f.stale) return 'live (push)';
  return f.lastUpdatedAt === null
    ? 'STALE, not loaded yet'
    : `STALE, updated ${formatTime(f.lastUpdatedAt)}`;
}

/** Whether any of the given sources currently has a failed read: a read that
 * fails AFTER an earlier success must not let the connection chip keep
 * reading healthy off that stale success. */
export function hasSourceErrors(
  errors: Partial<Record<WallboardSource, string>>,
  sources: readonly WallboardSource[]
): boolean {
  return sources.some((s) => errors[s] !== undefined);
}

export type ConnectionChipState = 'live' | 'connecting' | 'polling' | 'offline';

/**
 * The header connection chip (WI 34539 sim review, PR 4147 thread 49450): a
 * hub can report "connected" with zero data ever loaded, or after the
 * bridge rejected the session, so a bare hub-connected check reads healthy
 * on an unattended TV showing empty/STALE cards next to it. "live" requires
 * BOTH a connected hub AND at least one full successful read across every
 * source, with no source currently failing; a later read failure drops it
 * back out of "live" even though an earlier successful read is still on
 * screen.
 */
export function connectionChipState(input: {
  hubConnected: boolean;
  everFullyLoaded: boolean;
  hasErrors: boolean;
  pollingFallback: boolean;
}): ConnectionChipState {
  if (!input.hubConnected) return input.pollingFallback ? 'polling' : 'offline';
  return !input.everFullyLoaded || input.hasErrors ? 'connecting' : 'live';
}

/** Reload only after a sustained disconnect, and never faster than the floor. */
export function shouldReloadPage(input: {
  disconnectedSinceMs: number | null;
  lastReloadAtMs: number | null;
  nowMs: number;
}): boolean {
  if (input.disconnectedSinceMs === null) return false;
  if (input.nowMs - input.disconnectedSinceMs < RELOAD_AFTER_DISCONNECT_MS)
    return false;
  return (
    input.lastReloadAtMs === null ||
    input.nowMs - input.lastReloadAtMs >= MIN_RELOAD_INTERVAL_MS
  );
}
